/**
 * 与后端 FundingTaskImageUrlFields 对齐：Worker 拉取的参考图字段。
 * Guard 只扫这些键；工作流写 funding 任务 result 时走 buildPipelineReferenceResult。
 */
export const FUNDING_TASK_IMAGE_URL_SCALAR_KEYS = Object.freeze([
  'firstFrameUrl',
  'referenceImageUrl',
  'imageUrl',
  'previewUrl',
  'modelImageUrl',
]);

export const FUNDING_TASK_IMAGE_URL_LIST_KEYS = Object.freeze([
  'referenceUrls',
  'referencePreviews',
  'referenceImages',
  'modelImageUrls',
  'garmentImageUrls',
]);

export const FUNDING_TASK_SOURCE_IMAGE_URLS_KEY = 'sourceImageUrls';

const PIPELINE_SCALARS = ['firstFrameUrl', 'referenceImageUrl', 'imageUrl', 'previewUrl'];
const PIPELINE_LISTS = ['referenceUrls', 'referencePreviews', 'referenceImages'];

export function fundingTaskImageUrlFieldContract() {
  return {
    scalar: [...FUNDING_TASK_IMAGE_URL_SCALAR_KEYS],
    list: [...FUNDING_TASK_IMAGE_URL_LIST_KEYS],
    source: FUNDING_TASK_SOURCE_IMAGE_URLS_KEY,
    nested: ['videoUnits', 'imageGroups'],
  };
}

/**
 * 用流水线当前图填满 Guard 扫描槽位（不含模特/服装，以免误填）。
 */
export function buildPipelineReferenceResult({ imageUrl, imageId, previewUrl } = {}) {
  const url = String(imageUrl || '').trim();
  const preview = String(previewUrl || url).trim();
  const out = {};
  if (!url) return out;
  PIPELINE_SCALARS.forEach((key) => {
    out[key] = key === 'previewUrl' ? (preview || url) : url;
  });
  PIPELINE_LISTS.forEach((key) => {
    out[key] = [key === 'referencePreviews' ? (preview || url) : url];
  });
  const id = String(imageId || '').trim();
  if (id) {
    out[FUNDING_TASK_SOURCE_IMAGE_URLS_KEY] = { [id]: url };
  }
  return sanitizeFundingTaskResultForApi(out);
}

/** 后端 SafeImageUrlValidator 不接受 blob/indexeddb 等临时地址。 */
export function isPersistableTaskImageUrl(url) {
  const s = String(url || '').trim();
  if (!s) return false;
  if (s.startsWith('blob:') || s.startsWith('indexeddb://')) return false;
  if (s.startsWith('data:') && !s.startsWith('data:image/')) return false;
  return true;
}

/** 任务 result 写入顺序：持久化/OSS 优先，blob 预览仅作 UI 兜底。 */
const STUDIO_TASK_PERSISTED_URL_KEYS = Object.freeze([
  'url',
  'apiUrl',
  'src',
  'imageUrl',
  'serverUrl',
  'viewUrl',
  'displayUrl',
  'storedUrl',
  'previewUrl',
]);

/** 从工作室源图条目解析可提交后端的图片地址（POST /tasks Guard 白名单）。 */
export function resolveStudioTaskPersistedImageUrl(item) {
  if (item == null) return '';
  if (typeof item === 'string') {
    const s = item.trim();
    return isPersistableTaskImageUrl(s) ? s : '';
  }
  if (typeof item !== 'object') return '';
  for (const key of STUDIO_TASK_PERSISTED_URL_KEYS) {
    const s = String(item[key] || '').trim();
    if (isPersistableTaskImageUrl(s)) return s;
  }
  return '';
}

export function mapStudioTaskReferencePreviews(items) {
  return (Array.isArray(items) ? items : [])
    .map((item) => resolveStudioTaskPersistedImageUrl(item))
    .filter(Boolean);
}

export function resolveStudioTaskPreviewUrl(items, fallback = '') {
  const fromItems = mapStudioTaskReferencePreviews(items)[0];
  if (fromItems) return fromItems;
  const fb = String(fallback || '').trim();
  return isPersistableTaskImageUrl(fb) ? fb : '';
}

function collectReferenceFallbacks(result) {
  const out = [];
  const seen = new Set();
  const push = (v) => {
    const s = String(v || '').trim();
    if (!isPersistableTaskImageUrl(s) || seen.has(s)) return;
    seen.add(s);
    out.push(s);
  };
  (Array.isArray(result?.referenceUrls) ? result.referenceUrls : []).forEach(push);
  (Array.isArray(result?.referenceImages) ? result.referenceImages : []).forEach(push);
  (Array.isArray(result?.modelImageUrls) ? result.modelImageUrls : []).forEach(push);
  (Array.isArray(result?.garmentImageUrls) ? result.garmentImageUrls : []).forEach(push);
  push(result?.referenceImageUrl);
  push(result?.imageUrl);
  push(result?.firstFrameUrl);
  push(result?.modelImageUrl);
  push(result?.previewUrl);
  const src = result?.[FUNDING_TASK_SOURCE_IMAGE_URLS_KEY];
  if (src && typeof src === 'object' && !Array.isArray(src)) {
    Object.values(src).forEach(push);
  }
  return out;
}

function fallbackAt(fallbacks, index) {
  if (index >= 0 && index < fallbacks.length) return fallbacks[index];
  return fallbacks[0] || '';
}

function sanitizeScalar(raw, fallbacks) {
  const s = String(raw || '').trim();
  if (isPersistableTaskImageUrl(s)) return s;
  return fallbackAt(fallbacks, 0);
}

function sanitizeUrlList(list, key, fallbacks, parallelUrls) {
  if (!Array.isArray(list)) return list;
  const isPreview = key === 'referencePreviews' || key === 'previews';
  return list.map((item, index) => {
    const s = String(item || '').trim();
    if (isPersistableTaskImageUrl(s)) return s;
    if (isPreview) {
      const fromParallel = String(parallelUrls?.[index] || '').trim();
      if (isPersistableTaskImageUrl(fromParallel)) return fromParallel;
    }
    return fallbackAt(fallbacks, index);
  }).filter((s) => isPersistableTaskImageUrl(s));
}

function sanitizeNestedUnit(unit, fallbacks) {
  if (!unit || typeof unit !== 'object') return unit;
  const next = { ...unit };
  ['firstFrameUrl', 'referenceImageUrl'].forEach((key) => {
    if (typeof next[key] === 'string') {
      next[key] = sanitizeScalar(next[key], fallbacks);
    }
  });
  if (Array.isArray(next.referencePreviews)) {
    next.referencePreviews = sanitizeUrlList(
      next.referencePreviews,
      'referencePreviews',
      fallbacks,
      next.referenceUrls,
    );
  }
  return next;
}

/**
 * POST/PUT /api/tasks 前清洗 result：本地上传预览常用 blob:，会触发后端 SSRF 400。
 */
export function sanitizeFundingTaskResultForApi(result) {
  if (!result || typeof result !== 'object' || Array.isArray(result)) {
    return result;
  }
  const out = { ...result };
  const fallbacks = collectReferenceFallbacks(out);
  const referenceUrls = Array.isArray(out.referenceUrls) ? out.referenceUrls : [];

  FUNDING_TASK_IMAGE_URL_SCALAR_KEYS.forEach((key) => {
    if (typeof out[key] === 'string') {
      out[key] = sanitizeScalar(out[key], fallbacks);
    }
  });

  FUNDING_TASK_IMAGE_URL_LIST_KEYS.forEach((key) => {
    if (Array.isArray(out[key])) {
      out[key] = sanitizeUrlList(out[key], key, fallbacks, referenceUrls);
    }
  });

  const src = out[FUNDING_TASK_SOURCE_IMAGE_URLS_KEY];
  if (src && typeof src === 'object' && !Array.isArray(src)) {
    const next = { ...src };
    Object.keys(next).forEach((k) => {
      next[k] = sanitizeScalar(next[k], fallbacks);
    });
    out[FUNDING_TASK_SOURCE_IMAGE_URLS_KEY] = next;
  }

  if (Array.isArray(out.videoUnits)) {
    out.videoUnits = out.videoUnits.map((unit) => sanitizeNestedUnit(unit, fallbacks));
  }

  if (Array.isArray(out.imageGroups)) {
    out.imageGroups = out.imageGroups.map((group) => {
      const g = { ...group };
      if (Array.isArray(g.urls)) {
        g.urls = sanitizeUrlList(g.urls, 'urls', fallbacks, referenceUrls);
      }
      if (Array.isArray(g.previews)) {
        g.previews = sanitizeUrlList(g.previews, 'previews', fallbacks, g.urls || referenceUrls);
      }
      return g;
    });
  }

  return out;
}
